import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import colors from '../../theme/colors';

const ChildLabelsContainer = styled.div`
  display: none; /* Hidden on desktop */

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: wrap;
    gap: 0; /* No gap between boxes */
    position: absolute;
    top: 305px; /* Closer to buttons (10px up from 275px) */
    left: 50%;
    transform: translateX(-50%);
    width: 200px; /* Same as node width */
    z-index: 5;
    background: ${colors.purple.alpha35}; /* 2x more transparent (was 0.7) */
    border: 2px solid ${colors.border.lightWeak}; /* 2x more transparent */
    border-radius: 8px;
    overflow: hidden;
  }
`;

const ChildLabelBox = styled(motion.div)`
  background: ${colors.purple.alpha30}; /* 2x more transparent (was 0.6) */
  border-right: 1px solid ${colors.border.lightSubtle}; /* 2x more transparent */
  border-bottom: 1px solid ${colors.border.lightSubtle}; /* 2x more transparent */
  padding: 4px; /* Smaller padding */
  width: 50%; /* Exact half width */
  cursor: pointer;
  transition: all 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  /* Remove right border on odd items (right column) */
  &:nth-child(even) {
    border-right: none;
  }

  /* Remove bottom border on last row */
  &:nth-last-child(-n+2) {
    border-bottom: none;
  }

  &:hover {
    background: ${colors.accent.orange};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ChildLabelText = styled.div`
  color: ${colors.accent.orange95};
  font-size: 0.65rem; /* Smaller text */
  font-weight: 500;
  text-align: center;
  line-height: 1.2;
  word-wrap: break-word;
  transition: color 0.2s ease;

  ${ChildLabelBox}:hover & {
    color: ${colors.primary.dark95};
  }
`;

/**
 * Renders child node labels below parent nodes on mobile
 * Shows 2 labels per row, stacked vertically in a single container
 * Only appears if children are expanded/visible
 */
const MobileChildLabels = ({ node, allNodes, onNodeClick, expandedNodes }) => {
  // Only show if this node is expanded
  if (!expandedNodes || !expandedNodes.has(node.id)) return null;

  // Find all children of this node
  const children = allNodes.filter(n => n.parentId === node.id);

  if (children.length === 0) return null;

  return (
    <ChildLabelsContainer>
      {children.map(child => (
        <ChildLabelBox
          key={child.id}
          onClick={(e) => {
            e.stopPropagation();
            onNodeClick(child.id);
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <ChildLabelText>{child.title}</ChildLabelText>
        </ChildLabelBox>
      ))}
    </ChildLabelsContainer>
  );
};

export default MobileChildLabels;
